import { useEffect, useState } from "react";
import useStore from "../../../../context/useStore";
import { brandActions } from "../../../../context/actions/brandActions";
import { brandModelActions } from "../../../../context/actions/brandModelActions";
import { repurchaseService } from "../../../../services/repurchaseService";
import { DateTime } from "luxon";
import carburantTools from "../../../../helpers/carburantTools";

const initialFormState = {
  brandUuid: "",
  brandModelUuid: "",
  carburantId: "",
  date: DateTime.now().toISODate(),
  duree: "",
  kilometrage: "",
};

// Normalise les initialValues : si on reçoit un fuelTypeId (id numérique
// de FUEL_TYPES) depuis la liste des VR, on le convertit en carburantId
// (clé string de FLOTTE.CARBURANTS) en s'appuyant sur le `name` commun.
const normalizeInitialValues = (initialValues, fuelTypes, carburants) => {
  const init = { ...initialValues };
  if (init.fuelTypeId && !init.carburantId) {
    const carburantId = carburantTools.fuelTypeIdToCarburantId(
      init.fuelTypeId,
      fuelTypes,
      carburants
    );
    if (carburantId) init.carburantId = carburantId;
    delete init.fuelTypeId;
  }
  return init;
};

const useRepurchaseSimulator = (initialValues = {}) => {
  const [state, dispatch] = useStore();
  const b_actions = brandActions(state, dispatch);
  const bm_actions = brandModelActions(state, dispatch);

  const fuelTypes = state.constant.items?.FUEL_TYPES || [];
  const carburants = state.constant.items?.FLOTTE?.CARBURANTS || [];
  const modesResolution =
    state.constant.items?.FLOTTE?.MODES_RESOLUTION_VR || [];

  const [form, setForm] = useState({
    ...initialFormState,
    ...normalizeInitialValues(initialValues, fuelTypes, carburants),
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!state.brand.items?.length) b_actions.getAllBrands();
    if (!state.brandModel.items?.length) bm_actions.getAllBrandModels();
  }, []);

  const brands = state.brand.items || [];
  const allBrandModels = state.brandModel.items || [];
  const brandModels = allBrandModels.filter((bm) => {
    if (!form.brandUuid) return true;
    return bm.brand?.uuid === form.brandUuid;
  });

  const handleChange = (name, value) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };

      // Sélection d'un modèle → auto-sélectionne sa marque si absente/différente
      if (name === "brandModelUuid" && value) {
        const bm = allBrandModels.find((m) => m.uuid === value);
        if (bm?.brand?.uuid && bm.brand.uuid !== prev.brandUuid) {
          next.brandUuid = bm.brand.uuid;
        }
      }

      // Changement de marque → reset le modèle uniquement s'il n'appartient pas à la nouvelle marque
      if (name === "brandUuid" && prev.brandModelUuid) {
        const currentBm = allBrandModels.find(
          (m) => m.uuid === prev.brandModelUuid
        );
        if (!value || currentBm?.brand?.uuid !== value) {
          next.brandModelUuid = "";
        }
      }

      return next;
    });
    setResult(null);
    setError(null);
  };

  const canSubmit =
    form.brandUuid && form.brandModelUuid && form.carburantId && form.date;

  const submit = async () => {
    if (!canSubmit) return;
    setIsLoading(true);
    setError(null);
    setResult(null);

    const brand = brands.find((b) => b.uuid === form.brandUuid);
    const brandModel = brandModels.find(
      (bm) => bm.uuid === form.brandModelUuid
    );

    const params = {
      date: form.date,
      marque: brand?.name,
      modele: brandModel?.name,
      carburant: form.carburantId,
    };
    if (form.duree) params.duree = form.duree;
    if (form.kilometrage) params.kilometrage = form.kilometrage;

    try {
      const res = await repurchaseService.simulateGrille(params);
      setResult(res.data);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.detail ||
          "Aucune grille trouvée pour ces critères."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setForm({
      brandUuid: "",
      brandModelUuid: "",
      carburantId: "",
      date: "",
      duree: "",
      kilometrage: "",
    });
    setResult(null);
    setError(null);
  };

  return {
    form,
    result,
    error,
    isLoading,
    brands,
    brandModels,
    allBrandModels,
    carburants,
    modesResolution,
    canSubmit,
    handleChange,
    submit,
    reset,
  };
};

export default useRepurchaseSimulator;
